import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const NoticeList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') || 1));
  const q = searchParams.get('q') || '';

  const [keyword, setKeyword] = useState(q);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useEffect(() => {
    fetchNoticeList();
    fetchBadges();
  }, [page, q]);

  const fetchNoticeList = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/notices', {
        params: { page, q },
        withCredentials: true,
      });

      const data = res.data || {};
      setList(Array.isArray(data.list) ? data.list : []);
      setTotal(Number(data.total || 0));
      setTotalPages(Math.max(1, Number(data.total_pages || 1)));
    } catch (err) {
      console.error('공지사항 목록 조회 실패:', err);
      setList([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const [cartRes, wishlistRes] = await Promise.all([
        axios.get('/api/cart/count', { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
        axios.get('/api/wishlist/count', { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);

      setCartCount(Number(cartRes?.data?.count || 0));
      setWishlistCount(Number(wishlistRes?.data?.count || 0));
    } catch (err) {
      setCartCount(0);
      setWishlistCount(0);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const next = new URLSearchParams();
    const trimmed = keyword.trim();

    if (trimmed) next.set('q', trimmed);
    next.set('page', '1');

    setSearchParams(next);
  };

  const movePage = (targetPage) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(targetPage));
    setSearchParams(next);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return String(dateString).slice(0, 10);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    const pages = [];
    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }

    return (
      <nav className="mt-3">
        <ul className="pagination justify-content-center mb-0">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button
              type="button"
              className="page-link"
              onClick={() => movePage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              이전
            </button>
          </li>

          {pages.map((p) => (
            <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => movePage(p)}
              >
                {p}
              </button>
            </li>
          ))}

          <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
            <button
              type="button"
              className="page-link"
              onClick={() => movePage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              다음
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <>
      <style>{`
        .notice-list-page {
          background: #fff;
          min-height: 100vh;
          font-family: sans-serif;
          overflow-x: hidden;
        }

        .notice-top-bar {
          background: #ffffff;
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1100;
          font-weight: bold;
          font-size: 1.2rem;
          border-bottom: 1px solid #eee;
        }

        .notice-top-bar a {
          color: inherit;
          text-decoration: none;
        }

        .notice-icons {
          display: flex;
          gap: 1rem;
          font-size: 1.4rem;
          align-items: center;
        }

        .notice-page-wrap {
          max-width: 720px;
          margin: 0 auto;
          padding: 10px 15px 24px;
          box-sizing: border-box;
        }

        .notice-badge {
          position: absolute;
          top: 0;
          right: -6px;
          min-width: 18px;
          height: 18px;
          font-size: 10px;
          border-radius: 50%;
          line-height: 18px;
          text-align: center;
          padding: 0;
          background: #dc3545;
          color: #fff;
          font-weight: 700;
        }

        .notice-icon-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .notice-search-input:focus {
          box-shadow: none !important;
        }

        .notice-item {
          text-decoration: none;
        }

        .notice-item:hover {
          background: #fafafa;
        }

        .notice-arrow {
          color: #999;
          font-size: 1.1rem;
        }

        .pagination .page-link {
          color: #212529;
          box-shadow: none !important;
        }

        .pagination .page-item.active .page-link {
          background: #212529;
          border-color: #212529;
          color: #fff;
        }
      `}</style>

      <div className="notice-list-page">

        <div className="notice-page-wrap">
          <form className="mt-3 mb-3" onSubmit={handleSearch}>
            <div className="input-group">
              <input
                type="text"
                className="form-control notice-search-input"
                placeholder="공지 제목 검색"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <button className="btn btn-dark" type="submit">
                검색
              </button>
            </div>
          </form>

          {loading ? (
            <div className="text-center text-muted py-5">로딩 중...</div>
          ) : total <= 0 ? (
            <div className="text-center text-muted py-5">공지사항이 없습니다.</div>
          ) : (
            <div className="list-group">
              {list.map((row) => (
                <Link
                  key={row.id}
                  to={`/notice/${row.id}`}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center notice-item"
                >
                  <div className="me-2">
                    <div className="fw-semibold">{row.title}</div>
                    <div className="small text-muted">{formatDate(row.created_at)}</div>
                  </div>
                  <span className="notice-arrow">›</span>
                </Link>
              ))}
            </div>
          )}

          {renderPagination()}
        </div>
      </div>
    </>
  );
};

export default NoticeList;